import { useEffect, useRef } from "react";

/**
 * Hides the site header and toggles visibility of a sticky filter bar based
 * on scroll direction, but only once the filter bar has become "stuck" to
 * the top of the viewport.
 *
 * Attach `sentinelRef` to an empty marker element placed just above the
 * sticky filter bar, and `filterWrapperRef` to the filter bar itself. An
 * IntersectionObserver watches the sentinel to detect when the filter bar
 * has stuck (sentinel scrolled out of view). Only while stuck does scroll
 * direction toggle the "vanish"/"hidden" classes; scrolling back up (or
 * un-sticking) always clears both classes.
 *
 * @returns {{ sentinelRef: React.RefObject, filterWrapperRef: React.RefObject }}
 */
const useStickyFilterVanish = () => {
  const sentinelRef = useRef();
  const filterWrapperRef = useRef();

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const filterWrapper = filterWrapperRef.current;
    const header = document.querySelector("header#site-header");
    if (!sentinel || !header) return;

    let isStuck = false;
    let lastScrollY = window.scrollY;

    const onScroll = () => {
      if (!isStuck) return;
      const scrollingUp = window.scrollY < lastScrollY;
      lastScrollY = window.scrollY;
      header.classList.toggle("vanish", !scrollingUp);
      filterWrapper?.classList.toggle("hidden", scrollingUp);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        isStuck = !entry.isIntersecting;
        lastScrollY = window.scrollY;
        if (!isStuck) {
          header.classList.remove("vanish");
          filterWrapper?.classList.remove("hidden");
        }
      },
      { threshold: 0 }
    );

    observer.observe(sentinel);
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", onScroll);
      header.classList.remove("vanish");
      filterWrapper?.classList.remove("hidden");
    };
  }, []);

  return { sentinelRef, filterWrapperRef };
};

export default useStickyFilterVanish;
